import React, { FC, forwardRef } from 'react';
import { ProjectId } from 'api/models';
import { FieldOptionBase } from 'api/tasks';
import { Option } from 'components/atoms/select/types';
import {
  AssigneePickerFilter,
  useAssigneeOptions,
} from 'hooks/use-assignee-options';
import { Translate } from 'hooks/use-translation';
import { byLabel } from 'utils/comparators/by-label';
import { fullName } from 'utils/full-name';
import { getRandomColor } from 'utils/get-random-color';
import {
  AssigneeField,
  AssigneeValue,
  ProjectUsersByAccess,
  UsersMetadata,
} from '../fields';
import { EditableUserRelatedFieldProps } from './editable-props';
import { NoValue } from './no-value';
import { StyledSelect } from './styled-select';

interface Props extends EditableUserRelatedFieldProps<AssigneeValue[]> {
  field: AssigneeField;
}

interface OptionGroup {
  id?: string;
  label: string;
  options: Option[];
}

const empty: ProjectUsersByAccess = {
  private: {
    in_project_user_ids: [],
    not_in_project_user_ids: [],
  },
  shared: {
    in_project_user_ids: [],
    not_in_project_user_ids: [],
  },
};

export const getUserOptions = (
  translate: Translate,
  users: UsersMetadata,
  [, project_id]: ProjectId,
  is_private: boolean,
): OptionGroup[] => {
  const data = users.by_project_id[project_id] ?? empty;
  const { in_project_user_ids, not_in_project_user_ids } = is_private
    ? data.private
    : data.shared;

  return [
    {
      label: translate('users'),
      options: in_project_user_ids
        .filter((u) => users.non_external_user_ids.includes(u))
        .map((u) => users.by_user_id[u])
        .filter(Boolean)
        .map((u) => ({
          value: u.userId,
          label: fullName(u),
          imageUrl: u.photoUrl,
          description: u.role?.roleName,
          color: getRandomColor(u.userId),
        }))
        .sort(byLabel),
    },
    {
      label: translate('not_in_project'),
      options: not_in_project_user_ids
        .filter((u) => users.non_external_user_ids.includes(u))
        .map((u) => users.by_user_id[u])
        .filter(Boolean)
        .map((u) => ({
          value: u.userId,
          label: fullName(u),
          imageUrl: u.photoUrl,
          description: u.role?.roleName,
          color: getRandomColor(u.userId),
        }))
        .sort(byLabel),
    },
  ];
};

export const AssigneeFieldValue: FC<Props> = forwardRef(
  ({ field, is_private, project_id, editable, onChange }, ref) => {
    const options = useAssigneeOptions({
      projectId: project_id,
      isPrivate: is_private,
      filter: AssigneePickerFilter.All,
    });

    const flattened = options.reduce(
      (prev, x) => prev.concat(x.options),
      [] as Option[],
    );
    const assignees = (field.value ?? [])
      .map(AssigneeValue.toString)
      .map((x) => flattened.find((o) => o.value === x)!)
      .filter(Boolean);

    return !editable ? (
      field.value == null ? (
        <NoValue />
      ) : (
        <>{field.value}</>
      )
    ) : (
      <StyledSelect
        ref={ref}
        type="user"
        isMulti
        isClearable
        menuIsOpen
        options={options}
        value={assignees.sort(byLabel)}
        onChange={(latest: FieldOptionBase[]) =>
          onChange?.(
            (latest ?? [])
              .sort(byLabel)
              .map((x) => x.value.toString())
              .map(AssigneeValue.fromString),
          )
        }
        hideSelectedOptions={false}
      />
    );
  },
);
